import { useState, useRef, useEffect, useCallback } from 'react';
import { Model } from '@shared/types';

interface UsePlaceholderAnimationArgs {
  type: 'parametric' | 'imagetocad';
  imagesLength: number;
  placeholder: string;
  model: Model;
}

interface UsePlaceholderAnimationResult {
  placeholderAnim: string;
  placeholderOpacity: number;
}

// ------------------------------------------------------------
// Placeholder – Typed-out Animation
// When the target placeholder (based on mode & image state)
// changes, we progressively reveal each character so it looks
// like it's being typed in real-time. This gives users a more
// delightful sense of state change without abrupt flashes.
// ------------------------------------------------------------
//
// Extracted verbatim from TextAreaChat as a contiguous run of root hooks
// (computeTargetPlaceholder callback → placeholderAnim/placeholderOpacity
// state → placeholderRef → crossfade effect). Called in their place so the
// root's observable hook order is unchanged.
export function usePlaceholderAnimation({
  type,
  imagesLength,
  placeholder,
  model,
}: UsePlaceholderAnimationArgs): UsePlaceholderAnimationResult {
  // Helper to decide which placeholder we're targeting right now
  const computeTargetPlaceholder = useCallback(() => {
    if (type === 'imagetocad') {
      if (imagesLength > 0) return 'Edit uploaded image...';
      // Model-specific placeholders
      if (model === 'quality') return 'Make a rough 3D asset...';
      if (model === 'fast') return 'Make a textureless 3D asset...';
      if (model === 'ultra') return 'Make a production ready 3D asset...';
      if (model === 'trellis2' || model === 'trellis2alias')
        return 'Make a high-fidelity PBR 3D asset...';
      if (model === 'magneto4b')
        return 'Make a segmented CAD-ready 3D asset...';
      if (model === 'meshy7') return 'Make the most accurate 3D asset yet...';
      if (model === 'hunyuan') return 'Make a detailed 3D model...';
      if (model === 'tripo') return 'Make a textured 3D model...';
      return 'Speak anything into existence...';
    }
    return placeholder;
  }, [type, imagesLength, placeholder, model]);

  // The text currently shown in the placeholder (animates)
  const [placeholderAnim, setPlaceholderAnim] = useState('');
  const [placeholderOpacity, setPlaceholderOpacity] = useState(1);
  const placeholderRef = useRef('');

  // Shared helper that performs the crossfade animation
  const startCrossfade = (target: string) => {
    placeholderRef.current = target;

    // Start fade out
    setPlaceholderOpacity(0);

    // After fade out, update text and fade in
    setTimeout(() => {
      setPlaceholderAnim(target);
      setPlaceholderOpacity(1);
    }, 150);
  };

  // Kick off crossfade effect whenever the target placeholder changes
  useEffect(() => {
    const target = computeTargetPlaceholder();

    // If nothing has changed, make sure we're synced and bail.
    if (target === placeholderRef.current) {
      if (placeholderAnim !== target) {
        setPlaceholderAnim(target);
      }
      return;
    }

    startCrossfade(target);
  }, [
    type,
    imagesLength,
    placeholder,
    model,
    computeTargetPlaceholder,
    placeholderAnim,
  ]);

  return { placeholderAnim, placeholderOpacity };
}
